import { useEffect, useState } from 'react';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';
import { PermissionDenied, PermissionEnum, Popup, Tabs } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetActiveCurrenciesQuery,
  useLazyGetAdministrationCountriesQuery,
} from '../../store/dictionaries/service';
import { setIsDirty } from '../settings/store/slices/details';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const PspSettings = () => {
  const [linkPath, setLinkPath] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const match = useMatch(ROUTES.PSP);

  const {
    administrationDictionaries: {
      countries,
      activeCurrencies,
      activePermissions,
    },
    settings: {
      details: { isDirty },
    },
  } = useAppSelector((state) => state);

  const [getActiveCurrencies] = useLazyGetActiveCurrenciesQuery();
  const [getEmployeesCountries] = useLazyGetAdministrationCountriesQuery();

  useEffect(() => {
    if (!activeCurrencies) {
      getActiveCurrencies();
    }

    if (!countries) {
      getEmployeesCountries();
    }
  }, [countries, activeCurrencies]);

  useEffect(() => {
    let title = 'PSP details';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  useEffect(() => {
    if (
      match &&
      activePermissions?.includes(PermissionEnum.SETTINGS_SYSTEM_LIST)
    ) {
      navigate(ROUTES.PSP_DETAILS);
    }
  }, [match, activePermissions]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.SETTINGS_SYSTEM_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.pspSettings}>
        <div className={cn.settingsTitle}>PSP Settings</div>
        <div className={cn.settingsTabWrapper}>
          <Tabs
            itemClassName={cn.settingsTabItem}
            onRouterClick={(e) => {
              if (isDirty) {
                e.preventDefault();

                setDirtyPopupVisible(true);

                // @ts-ignore
                setLinkPath(e.target.href.replace(window.location.origin, ''));
              }
            }}
            tabs={[
              {
                name: 'PSP Details',
                path: ROUTES.PSP_DETAILS,
              },
              {
                name: 'Exchange rates',
                path: ROUTES.PSP_RATES,
              },
              {
                name: 'Webhooks',
                path: ROUTES.PSP_WEBHOOKS,
              },
              {
                name: 'Authorization',
                path: ROUTES.PSP_AUTHORIZATION,
              },
              {
                name: 'Brand',
                path: ROUTES.PSP_BRAND,
              },
              {
                name: 'OTP Settings',
                path: ROUTES.PSP_OTP,
              },
            ]}
            enableRouting
          />
        </div>
        <Outlet />
      </div>
    );
  }

  return (
    <>
      {content}
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => {
          setDirtyPopupVisible(false);
          setLinkPath('');
        }}
        onOkClick={() => {
          setDirtyPopupVisible(false);
          navigate(linkPath);
          dispatch(setIsDirty(false));
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
      />
    </>
  );
};

export default PspSettings;
